// The relay button every Flutter touch screen draws (fourTouchScreen.dart
// buildRaisedDeviceButton, and the identical copies in oneTouch / twoTouch):
//
//   • a ButtonColor → FontColor linear gradient whose DIRECTION flips with state
//     (bottom→top when on, top→bottom when off)
//   • 20px radius, and a shadow that changes with state: a soft FontColor glow
//     when on, a small black drop shadow when off
//   • the output name in bold black over a power icon
//   • the power icon is GREEN #66FF00 on, RED off, BLUE when the state is
//     unknown — see useRelayStatus for why unknown is its own colour
//   • long-press opens the Schedule screen for the output
//
// ── Button-settings marks ──────────────────────────────────────────────────
// Small badges saying what is CONFIGURED on this button (see
// core/devices/buttonStatus.js). Icons, not sentences — the tile is small.
//
// They STACK in two columns rather than taking a corner each: a button can
// carry several at once and there are only four corners.
//
//   left    clock + time    a schedule fires later today ("3:50 pm")
//           clock alone     a schedule exists, but nothing left today
//           timer + "3m"    switches itself off N minutes after a press
//
//   right   sync            paired with another switch (two-way)
//           router          restarts a minute after power returns (for a router)
//           power           does not remember its state through a power cut
//           swap + code     non-default All On / All Off, e.g. "off:no"
//
// ⚠️ TWO-WAY IS THE SYNC ICON, NOT THE ROUTER ONE. "Router" is the name of a
// Button State option — an output feeding a Wi-Fi router, power-cycled a minute
// after power returns. Drawing two-way with a router glyph made two unrelated
// settings look like the same thing. Flutter uses Icons.sync_alt for two-way
// and reserves the router meaning; so does this.

import React from 'react';
import { Box, ButtonBase, Tooltip, Typography, useTheme } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import RouterIcon from '@mui/icons-material/Router';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import useLongPress from '../useLongPress';
import { BUTTON_STATE_ROUTER } from '../../../core/devices/buttonStatus';

// A badge: icon, optional short label, on a dark pill so it stays legible over
// the tile's gradient (which runs light at one end whatever the theme).
function Mark({ icon, label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        px: label ? 0.5 : 0.25,
        py: 0.125,
        borderRadius: '8px',
        bgcolor: 'rgba(0,0,0,0.55)',
        color: '#fff',
        pointerEvents: 'none',
      }}
    >
      {icon}
      {label && (
        <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

const STACK = {
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

export const ON_GREEN = '#66FF00';

export function relayColor(isOn, isKnown) {
  if (!isKnown) return '#2196F3';
  return isOn ? ON_GREEN : '#F44336';
}

// Every mark the tile carries, in display order, split by side. One list feeds
// both layouts, so the corner stacks and the compact strip can never disagree.
function markList(marks, scheduled) {
  const left = [];
  const right = [];
  if (marks?.pending || scheduled || marks?.hasSchedule) {
    left.push({
      key: 'schedule',
      Icon: ScheduleIcon,
      label: marks?.pendingLabel || '',
      hint: marks?.pendingLabel ? `Schedule at ${marks.pendingLabel}` : 'Has a schedule',
    });
  }
  if (marks?.autoOff > 0) {
    left.push({
      key: 'autoOff', Icon: TimerOutlinedIcon, label: marks.autoOffLabel,
      hint: `Turns off ${marks.autoOffLabel} after a press`,
    });
  }
  if (marks?.partner) {
    right.push({ key: 'partner', Icon: SyncAltIcon, label: '', hint: 'Two-way with another switch' });
  }
  // The router option is the one worth spotting: that output cycles a minute
  // after power returns, so a light on it coming back late is correct rather
  // than faulty.
  if (marks?.buttonState) {
    const router = marks.buttonState.value === BUTTON_STATE_ROUTER;
    right.push({
      key: 'buttonState',
      Icon: router ? RouterIcon : PowerSettingsNewIcon,
      label: marks.buttonState.label,
      hint: `After power cut: ${marks.buttonState.label}`,
    });
  }
  // Without this a button left out of All Off silently ignores the bar above
  // it and looks broken.
  if (marks?.allBars) {
    right.push({
      key: 'allBars', Icon: SwapVertIcon, label: marks.allBars.label,
      hint: `All On / All Off: ${marks.allBars.label}`,
    });
  }
  return { left, right };
}

// ⚠️ COMPACT = the desktop room card (~58px tile). There the corner stacks sat
// ON the name — "off:no" alone is wider than half the tile, so "Single" read
// "Sing". In compact mode the marks become a strip of bare icons ABOVE the name,
// in the flow, so nothing can overlap; the words move into a tooltip.
function CompactMarks({ left, right }) {
  const all = [...left, ...right];
  if (!all.length) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: '3px', mb: '2px' }}>
      {all.map(({ key, Icon, hint }) => (
        <Tooltip key={key} title={hint} arrow disableInteractive>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              p: '1px',
              borderRadius: '6px',
              bgcolor: 'rgba(0,0,0,0.55)',
              color: '#fff',
            }}
          >
            <Icon sx={{ fontSize: 11 }} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

export default function RelayTile({
  name,
  isOn,
  isKnown,
  scheduled,
  marks,          // from buttonStatus.marksFor() — optional
  onToggle,
  onSchedule,
  width,
  height,
  iconSize,
  nameFont,
  compact = false, // small desktop tile: marks in a strip, not the corners
}) {
  const theme = useTheme();
  const c = theme.sli;
  const press = useLongPress(onSchedule, onToggle);
  const { left, right } = markList(marks, scheduled);

  return (
    <Box sx={{ position: 'relative', width, height }}>
      <ButtonBase
        {...press}
        title={compact ? name : undefined}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: compact ? '16px' : '20px',
          background: isOn
            ? `linear-gradient(to top, ${c.button}, ${c.text})`
            : `linear-gradient(to bottom, ${c.button}, ${c.text})`,
          boxShadow: isOn
            ? `0 0 18px 4px ${c.text}33`
            : '0 3px 6px rgba(0,0,0,0.2)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 0.75, minWidth: 0, maxWidth: '100%' }}>
          {compact && <CompactMarks left={left} right={right} />}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: nameFont,
              lineHeight: 1.15,
              color: '#000',
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: compact ? 1 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {name}
          </Typography>
          <Box sx={{ height: compact ? 4 : 8 }} />
          <PowerSettingsNewIcon sx={{ fontSize: iconSize, color: relayColor(isOn, isKnown) }} />
        </Box>
      </ButtonBase>

      {!compact && (
        <>
          {/* Left — "this switches itself". */}
          <Box sx={{ ...STACK, top: 6, left: 6, alignItems: 'flex-start' }}>
            {left.map(({ key, Icon, label }) => (
              <Mark key={key} icon={<Icon sx={{ fontSize: 14 }} />} label={label} />
            ))}
          </Box>

          {/* Right — "this behaves differently from the buttons beside it". */}
          <Box sx={{ ...STACK, top: 6, right: 6, alignItems: 'flex-end' }}>
            {right.map(({ key, Icon, label }) => (
              <Mark key={key} icon={<Icon sx={{ fontSize: 14 }} />} label={label} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
